'use client';

import { createContext, useContext, useMemo, useState, useEffect } from 'react';
import type { ReactNode } from 'react';
import { useUserProfile } from '../hooks/useUserProfile';
import { isLanguageSupported, getDefaultLanguage } from '../utils/languages';

// Load default language (English) synchronously for initial render
import en from '../translations/en.json';

type LocaleContextType = {
  locale: string;
  messages: any;
  t: (key: string) => string;
};

const LocaleContext = createContext<LocaleContextType | undefined>(undefined);

// Cache for loaded translations
const translationCache: Record<string, any> = {
  en, // Pre-load English as default
};

// Lazy load translation files
const loadTranslation = async (locale: string): Promise<any> => {
  // Return cached translation if available
  if (translationCache[locale]) {
    return translationCache[locale];
  }

  // Dynamically import translation file
  try {
    const translation = await import(`../translations/${locale}.json`);
    translationCache[locale] = translation.default || translation;
    return translationCache[locale];
  } catch (error) {
    console.warn(`Failed to load translation for locale: ${locale}`, error);
    // Fallback to English if translation fails to load
    return translationCache.en;
  }
};

export default function DynamicLocaleProvider({ children }: { children: ReactNode }) {
  const { language, loading, user } = useUserProfile();
  const [messages, setMessages] = useState<any>(en);
  const [currentLocale, setCurrentLocale] = useState<string>(getDefaultLanguage());

  // Determine effective locale
  const effectiveLocale = useMemo(() => {
    const defaultLocale = getDefaultLanguage();
    let locale = defaultLocale;
    
    // Priority order:
    // 1. If user is authenticated and has a language preference, use that
    // 2. Otherwise, check localStorage (for unauthenticated users or temporary overrides)
    // 3. Otherwise, use default
    
    if (!loading && user && language && isLanguageSupported(language)) {
      locale = language;
    } else {
      try {
        const storedLanguage = localStorage.getItem('language');
        if (storedLanguage && isLanguageSupported(storedLanguage)) {
          locale = storedLanguage;
        }
      } catch {
        // localStorage may not be available, use default
      }
    }
    
    return isLanguageSupported(locale) ? locale : defaultLocale;
  }, [language, loading, user]);

  // Load translation when locale changes
  useEffect(() => {
    // If locale is English and already cached, skip loading
    if (effectiveLocale === 'en' && translationCache.en) {
      setMessages(en);
      setCurrentLocale('en');
      return;
    }

    // If already loaded and cached, use cached version immediately
    if (effectiveLocale === currentLocale && translationCache[effectiveLocale]) {
      return;
    }

    // If translation is already cached, use it immediately (no loading state)
    if (translationCache[effectiveLocale]) {
      setMessages(translationCache[effectiveLocale]);
      setCurrentLocale(effectiveLocale);
      return;
    }

    // Need to load translation asynchronously
    loadTranslation(effectiveLocale)
      .then((loadedMessages) => {
        setMessages(loadedMessages);
        setCurrentLocale(effectiveLocale);
      })
      .catch((error) => {
        console.error('Failed to load translation:', error);
        // Fallback to English on error
        setMessages(en);
        setCurrentLocale('en');
      });
  }, [effectiveLocale, currentLocale]);

  const value = useMemo(() => {
    return {
      locale: currentLocale,
      messages,
      t: (key: string) => {
        const translation = messages[key] || key;
        return translation;
      }
    };
  }, [currentLocale, messages]);

  return (
    <LocaleContext.Provider value={value}>
      {children}
    </LocaleContext.Provider>
  );
}

export const useLocale = () => {
  const context = useContext(LocaleContext);
  if (!context) throw new Error('useLocale must be used inside DynamicLocaleProvider');
  return context;
};